/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { buildHashWithAuthMode, getLocationHash, serializeQueryOmittingAuthMode } from 'js/lib/authModeDeepLinkUtils';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Button } from '@coursera/cds-core';

import AuthenticationV3CtaTracking from 'bundles/authentication-common/components/AuthenticationV3CtaTracking';
import { useCloseMobileMenu } from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import type { AuthButtonProps } from 'bundles/composable-page-header/types';

const styles = {
  buttons: css`
    width: 100%;
    margin-top: var(--cds-spacing-200);
    max-width: unset;
  `,
  wrapper: css`
    z-index: 1000;
    position: fixed;
    background-color: var(--cds-color-neutral-background-primary-weak);
    bottom: 0;
    left: 0;
    width: 100%;
    box-shadow: 0 1px 6px 0 var(--cds-color-neutral-stroke-primary);
    padding-bottom: var(--cds-spacing-200);

    li {
      padding: 0 var(--cds-spacing-200);
      list-style-type: none;
    }
  `,
};

type MobileAuthMenuItemProps = {
  buttons: AuthButtonProps[];
};

const MobileAuthMenuItem = ({ buttons }: MobileAuthMenuItemProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const closeMenu = useCloseMobileMenu();

  const handleAuthClick = (authMode: 'signup' | 'login') => {
    const search = serializeQueryOmittingAuthMode(location.query);
    navigate({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
      hash: buildHashWithAuthMode(getLocationHash(location), authMode),
    });
    closeMenu?.();
  };

  return (
    <div css={styles.wrapper} className="mobile-auth-menu" data-testid="logged-out-auth-buttons-wrap">
      <AuthenticationV3CtaTracking>
        {(trackV3) => (
          <React.Fragment>
            {buttons.map(({ label, authMode }) => (
              <li key={`${authMode}_button_mobile`}>
                <Button
                  variant={authMode === 'signup' ? 'primary' : 'secondary'}
                  onClick={() => {
                    trackV3({ name: authMode, sectionName: 'page_header' });
                    handleAuthClick(authMode);
                  }}
                  role="menuitem"
                  css={styles.buttons}
                  data-e2e={authMode === 'signup' ? 'header-signup-button' : 'header-login-button'}
                >
                  {label}
                </Button>
              </li>
            ))}
          </React.Fragment>
        )}
      </AuthenticationV3CtaTracking>
    </div>
  );
};

export default MobileAuthMenuItem;
